import { Action, ActionInfo, Macro, MacroJson } from '@rapoo/device-sdk'

import { LOOP_MAX, LOOP_MODE_HOLD, LOOP_MODE_TOGGLE } from '../types'

/**
 * 固件没有 IgnoreDelay 字段，软件用 2ms 作为哨兵写入每个动作。
 * 回显时不能把这个哨兵展开成延迟卡片，否则坐标前会凭空多出一条延迟。
 */
const IGNORE_DELAY_SENTINEL_MS = 2

// 宏导入导出用的数据转化
export function useMacro() {
  const macroToJson = (macro: Macro): MacroJson => {
    const macroReal = {
      CycleNumber: 0,
      ID: 0,
      IgnoreDelay: false,
      InfoList: [] as ActionInfo[],
      Name: '',
      PlayType: 1,
    }

    if (macro?.loop != undefined) {
      // loop: 1~0xFFFD 指定次数 / 0xFFFE 指派切换 / 0xFFFF 按下播放
      if (macro.loop === LOOP_MODE_HOLD) {
        macroReal.PlayType = 3
      } else if (macro.loop === LOOP_MODE_TOGGLE) {
        macroReal.PlayType = 2
      } else if (macro.loop <= LOOP_MAX) {
        macroReal.CycleNumber = Math.max(0, macro.loop)
        macroReal.PlayType = 1
      }
    }
    if (macro?.index != undefined) {
      macroReal.ID = macro?.index
    }
    const ignoreDelay = macro?.ignoreDelay ?? false
    if (macro?.package) {
      for (let i = 0; i < macro.package.length; i++) {
        const packageData = macro.package[i]
        macroReal.InfoList.push(...formatAction(packageData, ignoreDelay))
      }
    }
    macroReal.IgnoreDelay = ignoreDelay
    macroReal.Name = macro.name

    return {
      Macro: macroReal,
      Platform: 'AHub',
    } as MacroJson
  }
  const formatAction = (packageData: Action, ignoreDelay = false): ActionInfo[] => {
    const action = {
      Action: 0,
      Delay: 0,
      KeyCode: 0,
      Type: 0,
      X: 0,
      Y: 0,
    }
    const actionArr = []
    // 当前正在处理的动作类型
    let type = 0
    if ([0xf0, 0xf1, 0xf2, 0xf3, 0xf4].includes(packageData.key)) {
      type = 1
    }
    if (packageData.key === 0xf7) {
      type = 3
    }
    if (packageData.key === 0xf8) {
      if (packageData.hWheel != 0) {
        type = 4
      }
      if (packageData.vWheel != 0) {
        type = 5
      }
    }
    if (type === 3) {
      actionArr.push({
        ...action,
        X: packageData.x,
        Y: packageData.y,
        Type: type,
      })
    } else if (type === 4) {
      actionArr.push({
        ...action,
        KeyCode: packageData.hWheel,
        Type: type,
      })
    } else if (type === 5) {
      actionArr.push({
        ...action,
        KeyCode: packageData.vWheel,
        Type: type,
      })
    } else {
      actionArr.push({
        ...action,
        Action: packageData.type,
        KeyCode: packageData.key,
        Type: type,
      })
    }

    const isIgnoreDelaySentinel = ignoreDelay && packageData.time === IGNORE_DELAY_SENTINEL_MS
    if (packageData?.time > 0 && !isIgnoreDelaySentinel) {
      type = 2
      actionArr.push({
        ...action,
        Delay: packageData.time,
        Type: type,
      })
    }

    return actionArr as ActionInfo[]
  }
  const jsonToMacro = (json: MacroJson): Macro => {
    const macroData = json.Macro
    const infoList = macroData.InfoList
    const packageList: Action[] = []

    let i = 0
    while (i < infoList.length) {
      const item = infoList[i]
      if (
        item.Type === 0 ||
        item.Type === 1 ||
        item.Type === 3 ||
        item.Type === 4 ||
        item.Type === 5
      ) {
        const action: Action = {
          time: 0,
          type: 0, // 默认值，Type3 无 Action 字段
          key: 0, // 默认值
        }

        // 区分不同起始 Type 的赋值逻辑
        if (item.Type === 0 || item.Type === 1) {
          // 按键/鼠标类型：赋值 Action 和 KeyCode
          action.type = item.Action
          action.key = item.KeyCode
        } else if (item.Type === 3) {
          // 坐标类型：赋值 x/y，固定 key=0xf7（和 macroToJson 对应）
          action.key = 0xf7
          action.x = item.X
          action.y = item.Y
        } else if (item.Type === 4) {
          action.key = 0xf8
          action.hWheel = item.KeyCode
          action.vWheel = 0
        } else if (item.Type === 5) {
          action.key = 0xf8
          action.vWheel = item.KeyCode
          action.hWheel = 0
        }

        i++ // 移动到下一项

        // 检查后续是否有延迟（Type === 2），无论起始 Type 是啥都要处理
        if (i < infoList.length && infoList[i].Type === 2) {
          action.time = infoList[i].Delay
          i++
        }

        packageList.push(action)
      } else {
        // 防御性跳过：单独的延迟/无效项
        i++
      }
    }

    // PlayType 转 loop 的逻辑不变
    let loop: number | undefined
    switch (macroData.PlayType) {
      case 1:
        loop = macroData.CycleNumber
        break
      case 2:
        loop = LOOP_MODE_TOGGLE
        break
      case 3:
        loop = LOOP_MODE_HOLD
        break
      default:
        loop = 0
    }

    return {
      name: macroData.Name,
      index: macroData.ID,
      loop: loop,
      package: packageList,
      ignoreDelay: macroData.IgnoreDelay,
    }
  }
  return {
    macroToJson,
    jsonToMacro,
  }
}
